import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import dayjs from "dayjs";

import {
  Box,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import {
  ADD_QUOTATION_TABLE_ROW,
  REMOVE_QUOTATION_TABLE_ROW,
  UPDATE_TABLE_ROW,
} from "../slice/QuotationTable";

import DeleteIcon from "@mui/icons-material/Delete";
import { handleKeyDown } from "src/views/utilities/enetrFunction";
import Loadable from "src/layouts/full/shared/loadable/Loadable";
import useFetchData from "src/views/utilities/useFetchData";

function BillTable() {
  const tableContainerRef = useRef();
  const { data, isLoading, error } = useFetchData(
    ["productData", "unitData"],
    ["/api/getProducts", "/api/getAdminMastersStatic"]
  );
  // console.log(data.unitData);

  const dispatch = useDispatch();
  const billTable = useSelector((state) => state.quotationTable);
  const billMaster = useSelector((state) => state.quotationMaster);


  useEffect(() => {
    const lastItem = billTable[billTable.length - 1];

    if (lastItem?.item !== null) {
      // Add a new row to the bill table
      dispatch(ADD_QUOTATION_TABLE_ROW());
    }
  }, [billTable, dispatch]);

  if (isLoading) return <Loadable />;
  if (error)
    return (
      <>
        <Loadable />
        <p>Error: {error.message}</p>
      </>
    );

  const handleProduct = (e, value, index) => {
    // Update the row in the table with product name and quantity
    dispatch(
      UPDATE_TABLE_ROW({
        index: index,
        item: value.productName,
        qty: 1,
      })
    );
  };

  const handleFocus = (event) => {
    event.target.select();
  };

  const handleDeleteRow = (index) => {
    if (index === 0 && billTable.length===1) {
      alert("Can't delete row");
    } else {
      dispatch(REMOVE_QUOTATION_TABLE_ROW({ index }));
    }
  };

  const handleUnitChange = (e, index) => {
    const findObj = data?.unitData?.data.find(
      (item) => item.main_sect === e.target.value
    );
    const daysToUpdate = Number(findObj.sub_sect); // e.g., 3
    const delDate = dayjs(billMaster?.date); // ensure it's a dayjs object

    const mergeDays = delDate.add(daysToUpdate, "day");
    dispatch(
      UPDATE_TABLE_ROW({
        index,
        type: e.target.value,
        deliveryDate: dayjs(mergeDays).format(),
      })
    );

    // console.log(dayjs(mergeDays).format());
  };

  return (
    <>
      <Box
        onKeyDown={handleKeyDown}
        sx={{
          background: "#fff",
          height: "46vh",
          overflow: "hidden",
          minWidth: 660,
        }}
      >
        <TableContainer sx={{ height: "100%" }} ref={tableContainerRef}>
          <Table
            stickyHeader
            size="small"
            aria-label="a dense table"
            sx={{ textAlign: "center" }}
          >
            <TableHead sx={{ bgcolor: "#01a3b6" }}>
              <TableRow>
                <TableCell
                  width={70}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",

                    color: "#fff",
                  }}
                >
                  Action
                </TableCell>
                <TableCell
                  width={70}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",

                    color: "#fff",
                  }}
                >
                  SiNo
                </TableCell>
                <TableCell
                  width={250}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",

                    color: "#fff",
                  }}
                >
                  Item
                </TableCell>
                <TableCell
                  width={200}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",
                    // minWidth: "10px",
                    // maxWidth: "10px",
                    color: "#fff",
                  }}
                >
                  Type
                </TableCell>

                <TableCell
                  width={100}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",

                    color: "#fff",
                  }}
                >
                  Qty
                </TableCell>
                   <TableCell
                  width={100}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",

                    color: "#fff",
                  }}
                >
                  Discount
                </TableCell>
                <TableCell
                  width={100}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",

                    color: "#fff",
                  }}
                >
                  Rate
                </TableCell>
                <TableCell
                  width={130}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",

                    color: "#fff",
                  }}
                >
                  Total
                </TableCell>
                <TableCell
                  width={200}
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",
                    // minWidth: "150px",
                    // maxWidth: "150px",
                    color: "#fff",
                  }}
                >
                  Delivery date
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    bgcolor: "#01a3b6",
                    minWidth: "150px",
                    maxWidth: "150px",
                    color: "#fff",
                  }}
                >
                  Note
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {billTable.map((item, index) => (
                <TableRow key={index} sx={{ borderBottom: "1px solid gray" }}>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <IconButton
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={() => handleDeleteRow(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    {index + 1}
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <Autocomplete
                      disableClearable
                      size="small"
                      fullWidth
                      disablePortal
                      options={data?.productData?.data}
                      getOptionLabel={(option) => option.productName || ""}
                      value={{ productName: item?.item || "" }}
                      onChange={(e, value) => handleProduct(e, value, index)}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <FormControl fullWidth size="small">
                      <Select
                        value={item?.type || ""}
                        // onChange={(e) =>
                        //   dispatch(
                        //     UPDATE_TABLE_ROW({
                        //       index,
                        //       type: e.target.value,
                        //     })
                        //   )
                        // }
                        onChange={(e) => handleUnitChange(e, index)}
                      >
                        {data?.unitData?.data.map((item, index) => (
                          <MenuItem key={index} value={item.main_sect}>
                            {item?.main_sect}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      onFocus={handleFocus}
                      value={item?.qty}
                      onChange={(e) =>
                        dispatch(
                          UPDATE_TABLE_ROW({
                            index,
                            qty: parseFloat(e.target.value) || 0,
                          })
                        )
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      onFocus={handleFocus}
                      value={item?.Dis}
                      onChange={(e) =>
                        dispatch(
                          UPDATE_TABLE_ROW({
                            index,
                            Dis: parseFloat(e.target.value) || 0,
                          })
                        )
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      onFocus={handleFocus}
                      value={item?.rate}
                      onChange={(e) =>
                        dispatch(
                          UPDATE_TABLE_ROW({
                            index,
                            rate: parseFloat(e.target.value) || 0,
                          })
                        )
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <TextField
                      fullWidth
                      type="number"
                      size="small"
                      onFocus={handleFocus}
                      value={item?.total}
                      onChange={(e) =>
                        dispatch(
                          UPDATE_TABLE_ROW({
                            index,
                            total: parseFloat(e.target.value) || 0,
                          })
                        )
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        onChange={(value) =>
                          dispatch(
                            UPDATE_TABLE_ROW({
                              index,
                              deliveryDate: dayjs(value).format(),
                            })
                          )
                        }
                        // onChange={(value) => handleDateChange(value)}
                        value={dayjs(item.deliveryDate)}
                        slotProps={{
                          textField: {
                            size: "small",
                            fullWidth: true,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField {...params} size="small" />
                        )}
                      />
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell
                    sx={{ px: 1, py: 0.2, borderBottom: "1px solid gray" }}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      value={item?.note || ""}
                      onChange={(e) =>
                        dispatch(
                          UPDATE_TABLE_ROW({
                            index,
                            note: e.target.value,
                          })
                        )
                      }
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );
}

export default BillTable;
